// One group of a lab report's reading, confirmed where it is on the Test (TGCLOUD-1868, plan D3;
// hazard 1868-H1): the gene list in "Genes tested", a variant's identity and its classification on
// its tile, the overall result in "Result".
//
// ONE CONFIRM, FOR ONE GROUP. The card's Confirm confirms its own group and nothing else
// (useReadingChecks.confirm takes one key), so friction follows risk: one action where the reader
// was confident, and where it was not, the values are typed or chosen first (the card's children)
// and Confirm waits for them. Confirm is enabled when the group needs nothing more: no contradiction
// left unchosen, and every low-confidence value of it holding one. What it still needs is said
// beside it. Once confirmed it says "Confirmed" and when; a later change to any value of the group
// takes the confirmation away (useGroupConfirmations), and the card asks again.
//
// Nothing outside a review: the card draws only while a reading is applied and can be confirmed.
//
// FOR THE KEYBOARD AND A SCREEN READER (review of the view). Confirm is replaced by "Confirmed at
// 14:05" once pressed, so focus would go with the button, to the dialog's root, and the clinician
// would start again from its first control. It moves to that line instead, a status, so the
// confirmation is announced and Tab goes on from the card. Each Confirm is named with its part of
// the report (`name`, "Confirm the variant: MLH1 c.1852_1854del"): a report with two variants used
// to offer "Confirm the variant" twice.
import React from 'react';
import moment from 'moment';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useReadingChecksContext } from './useReadingChecks';
import {
  BEFORE_CONFIRMING,
  confirmedAtText
} from './labReportVerificationVocabulary';

/** "14:05": a confirmation's time, by the reader's own clock (it is an instant). */
export function confirmedTime(at) {
  const instant = toLocalDateFromInstant(at);
  return instant ? moment(instant).format('HH:mm') : '';
}

/**
 * @param {object} props
 * @param {string} props.groupKey      the reading's group key
 * @param {string} props.title         the group in words, translated by the caller
 * @param {string} props.confirmLabel  the Confirm action's words, translated by the caller
 * @param {string} [props.name]        which one, where a report can have several (a variant):
 *   added to Confirm's accessible name after its visible words
 * @param {React.ReactNode} [props.children]  the group's own lines: what it is, the values to enter
 * @param {object} [props.sx]
 */
export default function GroupCard({
  groupKey,
  title,
  confirmLabel,
  name = null,
  children,
  sx
}) {
  const t = useAutoTranslation();
  const checks = useReadingChecksContext();
  const statusRef = React.useRef(null);
  // Confirm was pressed here: once the card says it is confirmed, focus goes to that line.
  const pressed = React.useRef(false);
  const confirmed =
    !!checks && checks.active && !!checks.group(groupKey)
      ? checks.isConfirmed(groupKey)
      : false;
  React.useEffect(() => {
    if (!confirmed || !pressed.current) return;
    pressed.current = false;
    statusRef.current?.focus?.({ preventScroll: true });
  }, [confirmed]);
  if (!checks || !checks.active || !checks.group(groupKey)) return null;
  const needs = confirmed ? [] : checks.needs(groupKey);
  const at = checks.confirmedAt(groupKey);
  const titleId = `group-card-${groupKey}`;
  return (
    <Box
      data-testid="group-card"
      data-group-key={groupKey}
      data-confirmed={confirmed ? 'true' : 'false'}
      sx={{
        mt: 1,
        p: 1,
        border: '1px solid',
        borderColor: confirmed ? 'success.main' : 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        ...sx
      }}
    >
      <Typography
        component="div"
        variant="body2"
        id={titleId}
        sx={{ fontWeight: 700, mb: children ? 0.5 : 0 }}
      >
        {title}
      </Typography>
      {children}
      {needs.length > 0 && (
        <Box data-testid="group-needs" sx={{ mt: 0.75 }}>
          <Typography
            variant="caption"
            component="div"
            sx={{ fontWeight: 600 }}
          >
            {t(BEFORE_CONFIRMING)}
          </Typography>
          {needs.map((need) => (
            <Typography
              key={need}
              variant="caption"
              component="div"
              sx={{ color: 'text.secondary' }}
            >
              {need}
            </Typography>
          ))}
        </Box>
      )}
      <Box
        sx={{
          mt: 0.75,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        {confirmed ? (
          <Typography
            ref={statusRef}
            variant="body2"
            data-testid="group-confirmed"
            role="status"
            tabIndex={-1}
            aria-describedby={titleId}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'success.dark',
              fontWeight: 600,
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2
              }
            }}
          >
            <CheckCircleOutlinedIcon fontSize="small" aria-hidden="true" />
            {confirmedAtText(confirmedTime(at), t)}
          </Typography>
        ) : (
          <Button
            size="small"
            variant="contained"
            data-testid="confirm-group"
            data-group-key={groupKey}
            disabled={needs.length > 0}
            aria-label={name ? `${confirmLabel}: ${name}` : undefined}
            aria-describedby={titleId}
            onClick={() => {
              pressed.current = true;
              checks.confirm(groupKey);
            }}
          >
            {confirmLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}
